import { useCallback, useMemo, useReducer } from 'react';
import {
  buildMemberSubsetScope,
  descendantKeys,
  MemberIndex,
  MemberSubsetOp,
  MemberSubsetScope,
  seedExpansion
} from 'src/components/star-grid/utils/memberSubset';

/**
 * The subset editor's draft state.
 *
 * `expanded` and `checked` hold POSITION keys (root→member id chains), never bare member ids: a
 * multi-parent member appears at several positions, and acting on it under one parent must not act
 * on it under another.
 *
 * `scope` is the built subset — `null` means "all members", i.e. unrestricted, which is how the API
 * reads an empty selection. Two interaction styles:
 *   * while `scope` is null the pane is the whole hierarchy and `checked` IS the membership, so
 *     ticking boxes and pressing OK works as a plain picker;
 *   * once an operation has run, the pane is the scope and its VISIBLE ROWS are the membership (as
 *     in the TM1 editor), and `checked` becomes a selection tool for further operations.
 * Operations clear `checked` so the two never disagree about what is selected.
 */
export interface MemberSubsetDraft {
  readonly expanded: ReadonlySet<string>;
  readonly checked: ReadonlySet<string>;
  readonly scope: MemberSubsetScope | null;
  /** Positions removed from the view. Kept beside the scope so Remove survives an operation. */
  readonly excluded: ReadonlySet<string>;
}

export type MemberSubsetAction =
  | { readonly type: 'toggleExpanded'; readonly key: string }
  | { readonly type: 'setExpanded'; readonly key: string; readonly expanded: boolean }
  | { readonly type: 'toggleChecked'; readonly key: string }
  | { readonly type: 'setChecked'; readonly keys: readonly string[] }
  | { readonly type: 'applyOp'; readonly op: MemberSubsetOp; readonly selected: readonly string[]; readonly index: MemberIndex }
  | { readonly type: 'remove'; readonly keys: readonly string[]; readonly withDescendants: boolean; readonly index: MemberIndex }
  | { readonly type: 'reset' };

const toggle = (set: ReadonlySet<string>, key: string): ReadonlySet<string> => {
  const next = new Set(set);
  if (!next.delete(key)) next.add(key);
  return next;
};

export const EMPTY_MEMBER_SUBSET: MemberSubsetDraft = {
  expanded: new Set<string>(),
  checked: new Set<string>(),
  scope: null,
  excluded: new Set<string>()
};

/** Pure, so every operation is unit-testable without a network or a DOM. */
export const memberSubsetReducer = (state: MemberSubsetDraft, action: MemberSubsetAction): MemberSubsetDraft => {
  switch (action.type) {
    case 'toggleExpanded':
      return { ...state, expanded: toggle(state.expanded, action.key) };
    case 'setExpanded': {
      // Absolute, not a toggle: this mirrors the grid's own state, and a toggle would drift out of
      // step the moment an event arrived twice.
      if (state.expanded.has(action.key) === action.expanded) return state;
      return { ...state, expanded: toggle(state.expanded, action.key) };
    }
    case 'toggleChecked':
      return { ...state, checked: toggle(state.checked, action.key) };
    case 'setChecked': {
      // Wholesale, because ticking a parent cascades to every descendant — reconciling that one
      // toggle at a time would take a stream of events to land on the right answer.
      const next = new Set(action.keys);
      if (next.size === state.checked.size && [...next].every((key) => state.checked.has(key))) return state;
      return { ...state, checked: next };
    }
    case 'applyOp': {
      const scope = buildMemberSubsetScope(action.index, action.op, action.selected);
      if (scope.anchors.length === 0) return state;
      // Expansion is REPLACED, not merged: the operation decides how far open the pane starts, and
      // whatever the user had expanded while browsing elsewhere is no longer in view.
      // Exclusions are cleared: they were removals from the PREVIOUS view, and carrying them into a
      // freshly scoped one would hide members the user never removed from it.
      return { scope, expanded: seedExpansion(action.index, scope), checked: new Set<string>(), excluded: new Set<string>() };
    }
    case 'remove': {
      const next = new Set(state.excluded);
      for (const key of action.keys) {
        next.add(key);
        if (action.withDescendants) for (const descendant of descendantKeys(action.index, key)) next.add(descendant);
      }
      // Removed rows cannot stay ticked, or they would still be committed as members.
      const checked = new Set([...state.checked].filter((key) => !next.has(key)));
      return { ...state, excluded: next, checked };
    }
    case 'reset':
      return EMPTY_MEMBER_SUBSET;
    default:
      return state;
  }
};

/** Draft state for one open subset editor. Discarded on Cancel; the caller commits on OK. */
export const useMemberSubsetDraft = (initial: MemberSubsetDraft = EMPTY_MEMBER_SUBSET) => {
  const [draft, dispatch] = useReducer(memberSubsetReducer, initial);
  const toggleExpanded = useCallback((key: string) => dispatch({ type: 'toggleExpanded', key }), []);
  const setExpanded = useCallback((key: string, expanded: boolean) => dispatch({ type: 'setExpanded', key, expanded }), []);
  const toggleChecked = useCallback((key: string) => dispatch({ type: 'toggleChecked', key }), []);
  const setChecked = useCallback((keys: readonly string[]) => dispatch({ type: 'setChecked', keys }), []);
  const applyOp = useCallback(
    (op: MemberSubsetOp, selected: readonly string[], index: MemberIndex) => dispatch({ type: 'applyOp', op, selected, index }),
    []
  );
  const remove = useCallback(
    (keys: readonly string[], withDescendants: boolean, index: MemberIndex) => dispatch({ type: 'remove', keys, withDescendants, index }),
    []
  );
  const reset = useCallback(() => dispatch({ type: 'reset' }), []);
  return useMemo(
    () => ({ draft, dispatch, toggleExpanded, setExpanded, toggleChecked, setChecked, applyOp, remove, reset }),
    [draft, toggleExpanded, setExpanded, toggleChecked, setChecked, applyOp, remove, reset]
  );
};
